import React, { useState, useCallback } from 'react';
import {
  Drawer,
  Stack,
  Text,
  Group,
  Button,
  Badge,
  Alert,
  Box,
  ScrollArea,
  Card,
  Avatar,
  Timeline,
  ActionIcon,
  Menu,
  Modal,
  Loader
} from '@mantine/core';
import {
  IconX,
  IconEdit,
  IconTrash,
  IconClock,
  IconMapPin,
  IconUser,
  IconBuilding,
  IconCalendar,
  IconAlertTriangle,
  IconCheck,
  IconDots,
  IconPhone,
  IconMail,
  IconHistory,
  IconRefresh,
  IconExternalLink,
  IconBulb
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { EditAssignmentModal } from '../modals';
import { useChangeLogs, formatChangeLog } from '../../hooks/api/useChangeLogs';
import { usePublishAssignment } from '../../hooks/api/useAssignments';

// =============================================================================
// TYPES AND INTERFACES
// =============================================================================

interface AssignmentDetails {
  id: string;
  title: string;
  description?: string;
  room: {
    id: string;
    number: string;
    name: string;
    capacity: number;
    floor: {
      id: string;
      name: string;
      building: {
        id: string;
        name: string;
      };
    };
  };
  user?: {
    id: string;
    name: string;
    email: string;
    phone?: string;
    specialty?: string;
    department?: string;
    avatar?: string;
  };
  users?: {
    id: string;
    name: string;
    email?: string;
    phone?: string;
    specialty?: string;
    department?: string;
  };
  doctor?: {
    id: string;
    name: string;
    fullName: string;
    specialty?: string;
    department?: string;
  };
  sessionTime?: {
    id: string;
    name: string;
    startTime: string;
    endTime: string;
  };
  session_times?: {
    id: string;
    name: string;
    startTime: string;
    endTime: string;
    bufferMinutes: number;
  };
  roomSessionTime?: {
    id: string;
    name: string;
    label: string;
    startMinutes: string;
    endMinutes: string;
  };
  date: string;
  status: 'pending' | 'confirmed' | 'cancelled' | 'completed';
  conflictCode?: string;
  conflicts?: string[];
  createdAt: string;
  updatedAt: string;
  publishedAt?: string;
}

interface Doctor {
  id: string;
  name: string;
  email?: string;
  specialty?: string;
  department?: string;
}

interface SessionTime {
  id: string;
  name: string;
  startTime: string;
  endTime: string;
}

interface AssignmentDetailsDrawerProps {
  opened: boolean;
  onClose: () => void;
  assignment: AssignmentDetails | null;
  onEdit?: (assignment: AssignmentDetails) => void;
  onDelete?: (assignmentId: string) => Promise<void>;
  onUnassign?: (assignmentId: string) => Promise<void>;
  onViewRoom?: (roomId: string) => void;
  onViewDoctor?: (userId: string) => void;
  loading?: boolean;
  doctors?: Doctor[];
  sessionTimes?: SessionTime[];
}

/**
 * D1: Assignment Details Drawer
 * 
 * Features from UX Flow Spec:
 * - Assignment information display
 * - Doctor details with contact info
 * - Room and session information
 * - Status and conflict indicators
 * - Edit/delete actions
 * - Conflict resolution suggestions
 * - History and audit trail
 * - Quick actions for related entities
 */
const AssignmentDetailsDrawer: React.FC<AssignmentDetailsDrawerProps> = ({
  opened,
  onClose,
  assignment,
  onEdit,
  onDelete,
  onUnassign,
  onViewRoom,
  onViewDoctor,
  doctors = [],
  sessionTimes = []
}) => {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [unassigning, setUnassigning] = useState(false);
  
  // Publish assignment hook
  const publishMutation = usePublishAssignment();

  /**
   * Handle publish assignment
   * @param assignmentId - Assignment ID to publish
   */
  const handlePublishAssignment = async (assignmentId: string): Promise<void> => {
    try {
      await publishMutation.mutateAsync(assignmentId);
      notifications.show({
        title: 'Success',
        message: 'Assignment published successfully',
        color: 'green',
        icon: <IconCheck size={16} />
      });
      onClose(); // Close drawer after successful publish
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to publish assignment',
        color: 'red',
        icon: <IconX size={16} />
      });
    }
  };

  // Fetch change logs for this assignment (ADDED: 08-10-2025 19:03:45)
  const {
    data: changeLogsData,
    isLoading: logsLoading,
    error: logsError
  } = useChangeLogs(
    'assignment',
    assignment?.id || '',
    { limit: 50 },
    { enabled: !!assignment?.id }
  );

  // Helper to get user data from either user, users, or doctor field
  const getUserData = useCallback(() => {
    if (!assignment) return null;
    return assignment.user || assignment.users || assignment.doctor || null;
  }, [assignment]);

  const handleDelete = useCallback(async () => {
    if (!assignment || !onDelete) return;

    setDeleting(true);
    try {
      await onDelete(assignment.id);
      notifications.show({
        title: 'Assignment Deleted',
        message: `Assignment "${assignment.title}" has been deleted successfully`,
        color: 'green',
        icon: <IconCheck size={16} />
      });
      setShowDeleteModal(false);
      onClose();
    } catch (error) {
      notifications.show({
        title: 'Delete Failed',
        message: error instanceof Error ? error.message : 'Failed to delete assignment',
        color: 'red',
        icon: <IconX size={16} />
      });
    } finally {
      setDeleting(false);
    }
  }, [assignment, onDelete, onClose]);

  const handleUnassign = useCallback(async () => {
    if (!assignment || !onUnassign) return;

    setUnassigning(true);
    try {
      await onUnassign(assignment.id);
      notifications.show({
        title: 'Assignment Unassigned',
        message: `Dr. ${getUserData()?.name || 'User'} has been unassigned from this session`,
        color: 'green',
        icon: <IconCheck size={16} />
      });
      onClose();
    } catch (error) {
      notifications.show({
        title: 'Unassignment Failed',
        message: error instanceof Error ? error.message : 'Failed to unassign doctor',
        color: 'red',
        icon: <IconX size={16} />
      });
    } finally {
      setUnassigning(false);
    }
  }, [assignment, onUnassign, onClose]);

  const getStatusColor = (status: string) => {
    const statusLower = status?.toLowerCase();
    switch (statusLower) {
      case 'confirmed': return 'green';
      case 'pending': return 'yellow';
      case 'cancelled': return 'red';
      case 'completed': return 'blue';
      default: return 'gray';
    }
  };

  const getStatusLabel = (status: string) => {
    const statusLower = status?.toLowerCase();
    switch (statusLower) {
      case 'confirmed': return 'Confirmed';
      case 'cancelled': return 'Cancelled';
      default: return status || 'Unknown';
    }
  };

  const formatTime = (timeString: string) => {
    return new Date(`2000-01-01T${timeString}`).toLocaleTimeString([], { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  if (!assignment) return null;

  return (
    <>
      <Drawer
        opened={opened}
        onClose={onClose}
        title={
          <Group gap="sm">
            <IconCalendar size={20} />
            <Text fw={600}>Assignment Details</Text>
            <Badge
              size="sm"
              variant="light"
              color={getStatusColor(assignment.status)}
            >
              {getStatusLabel(assignment.status)}
            </Badge>
          </Group>
        }
        size="md"
        position="right"
        withOverlay
        overlayProps={{ opacity: 0.5, blur: 3 }}
        closeOnClickOutside={false}
        closeOnEscape={!deleting && !unassigning}
        trapFocus
        returnFocus
      >
        <ScrollArea h="100%" scrollbarSize={6}>
          <Stack gap="md" p="md">
            {/* Assignment Header */}
            <Card withBorder p="md" radius="md">
              <Stack gap="sm">
                <Group justify="space-between" align="flex-start">
                  <Box style={{ flex: 1 }}>
                    <Text fw={600} size="lg">{assignment.title}</Text>
                    {assignment.description && (
                      <Text size="sm" c="dimmed" mt="xs">
                        {assignment.description}
                      </Text>
                    )}
                  </Box>
                  <Menu shadow="md" width={200}>
                    <Menu.Target>
                      <ActionIcon variant="light" size="sm">
                        <IconDots size={16} />
                      </ActionIcon>
                    </Menu.Target>
                    <Menu.Dropdown>
                      <Menu.Item
                        leftSection={<IconEdit size={16} />}
                        onClick={() => setShowEditModal(true)}
                        disabled={!onEdit}
                      >
                        Edit Assignment
                      </Menu.Item>
                      <Menu.Item
                        leftSection={<IconTrash size={16} />}
                        color="red"
                        onClick={() => setShowDeleteModal(true)}
                        disabled={!onDelete}
                      >
                        Delete Assignment
                      </Menu.Item>
                    </Menu.Dropdown>
                  </Menu>
                </Group>

                {/* Date and Time */}
                <Group gap="md">
                  <Group gap="xs">
                    <IconCalendar size={16} color="var(--mantine-color-blue-6)" />
                    <Text size="sm" fw={500}>
                      {new Date(assignment.date).toLocaleDateString()}
                    </Text>
                  </Group>
                  <Group gap="xs">
                    <IconClock size={16} color="var(--mantine-color-green-6)" />
                    <Text size="sm">
                      {formatTime(assignment.sessionTime?.startTime || assignment.session_times?.startTime || '00:00')} - {formatTime(assignment.sessionTime?.endTime || assignment.session_times?.endTime || '00:00')}
                    </Text>
                  </Group>
                </Group>
              </Stack>
            </Card>

            {/* Doctor Information */}
            <Card withBorder p="md" radius="md">
              <Stack gap="md">
                <Group justify="space-between" align="center">
                  <Group gap="sm">
                    <IconUser size={20} />
                    <Text fw={600}>Assigned Doctor</Text>
                  </Group>
                  <Button
                    variant="light"
                    size="xs"
                    leftSection={<IconExternalLink size={14} />}
                    onClick={() => getUserData() && onViewDoctor?.(getUserData()!.id)}
                  >
                    View Profile
                  </Button>
                </Group>

                <Group gap="md" align="flex-start">
                  <Avatar
                    size="lg"
                    radius="md"
                    src={(getUserData() as any)?.avatar || null}
                    color="blue"
                  >
                    {getUserData()?.name.split(' ').map(n => n[0]).join('') || 'U'}
                  </Avatar>
                  
                  <Box style={{ flex: 1 }}>
                    <Text fw={500} size="sm">{getUserData()?.name || 'Unknown'}</Text>
                    {getUserData()?.specialty && (
                      <Text size="xs" c="dimmed">{getUserData()!.specialty}</Text>
                    )}
                    {getUserData()?.department && (
                      <Text size="xs" c="dimmed">{getUserData()!.department}</Text>
                    )}
                    
                    <Group gap="md" mt="xs">
                      <Group gap="xs">
                        <IconMail size={14} color="var(--mantine-color-gray-6)" />
                        <Text size="xs">{(getUserData() as any)?.email || 'No email'}</Text>
                      </Group>
                      {(getUserData() as any)?.phone && (
                        <Group gap="xs">
                          <IconPhone size={14} color="var(--mantine-color-gray-6)" />
                          <Text size="xs">{(getUserData() as any)!.phone}</Text>
                        </Group>
                      )}
                    </Group>
                  </Box>
                </Group>
              </Stack>
            </Card>

            {/* Room Information */}
            <Card withBorder p="md" radius="md">
              <Stack gap="md">
                <Group justify="space-between" align="center">
                  <Group gap="sm">
                    <IconMapPin size={20} />
                    <Text fw={600}>Room Details</Text>
                  </Group>
                  <Button
                    variant="light"
                    size="xs"
                    leftSection={<IconExternalLink size={14} />}
                    onClick={() => onViewRoom?.(assignment.room.id)}
                  >
                    View Room
                  </Button>
                </Group>

                <Stack gap="xs">
                  <Group gap="md">
                    <Box>
                      <Text size="sm" fw={500}>{assignment.room.number}</Text>
                      <Text size="xs" c="dimmed">{assignment.room.name}</Text>
                    </Box>
                    <Badge size="sm" variant="light" color="blue">
                      Capacity: {assignment.room.capacity}
                    </Badge>
                  </Group>

                  <Group gap="xs" c="dimmed">
                    <IconBuilding size={14} />
                    <Text size="xs">
                      {assignment.room.floor.building.name} - {assignment.room.floor.name}
                    </Text>
                  </Group>
                </Stack>
              </Stack>
            </Card>

            {/* Session Information */}
            <Card withBorder p="md" radius="md">
              <Stack gap="md">
                <Group gap="sm">
                  <IconClock size={20} />
                  <Text fw={600}>Session Details</Text>
                </Group>

                <Group gap="md">
                  <Box>
                    <Text size="sm" fw={500}>{assignment.sessionTime?.name || assignment.session_times?.name}</Text>
                    <Text size="xs" c="dimmed">
                      {formatTime(assignment.sessionTime?.startTime || assignment.session_times?.startTime || '00:00')} - {formatTime(assignment.sessionTime?.endTime || assignment.session_times?.endTime || '00:00')}
                    </Text>
                  </Box>
                </Group>
              </Stack>
            </Card>

            {/* Conflicts */}
            {assignment.conflicts && assignment.conflicts.length > 0 && (
              <Alert
                icon={<IconAlertTriangle size={16} />}
                title="Conflicts Detected"
                color="red"
                variant="light"
              >
                <Stack gap="xs">
                  {assignment.conflicts.map((conflict, index) => (
                    <Text key={index} size="sm">• {conflict}</Text>
                  ))}
                </Stack>
              </Alert>
            )}

            {/* Actions */}
            <Card withBorder p="md" radius="md">
              <Stack gap="sm">
                <Text fw={600} size="sm">Quick Actions</Text>
                <Group gap="sm">
                  <Button
                    variant="light"
                    color="orange"
                    leftSection={<IconX size={16} />}
                    onClick={handleUnassign}
                    loading={unassigning}
                    disabled={!onUnassign || assignment.status === 'cancelled'}
                    size="sm"
                    style={{ flex: 1 }}
                  >
                    Unassign
                  </Button>
                  <Button
                    variant="light"
                    leftSection={<IconEdit size={16} />}
                    onClick={() => setShowEditModal(true)}
                    disabled={!onEdit}
                    size="sm"
                    style={{ flex: 1 }}
                  >
                    Edit
                  </Button>
                </Group>
              </Stack>
            </Card>

            {/* History */}
            <Card withBorder p="md" radius="md">
              <Stack gap="md">
                <Group gap="sm" justify="space-between">
                  <Group gap="sm">
                    <IconHistory size={20} />
                    <Text fw={600}>History</Text>
                  </Group>
                  {logsLoading && <Loader size="xs" />}
                </Group>

                {logsError ? (
                  <Alert color="red" icon={<IconAlertTriangle size={16} />}>
                    <Text size="sm">Failed to load audit trail</Text>
                  </Alert>
                ) : logsLoading ? (
                  <Text size="xs" c="dimmed">Loading audit trail...</Text>
                ) : changeLogsData?.data?.logs && changeLogsData.data.logs.length > 0 ? (
                  <Timeline active={-1} bulletSize={20} lineWidth={2}>
                    {changeLogsData.data.logs.map((log) => {
                      const formatted = formatChangeLog(log);
                      const Icon = formatted.action === 'CREATE' ? IconCheck 
                                 : formatted.action === 'UPDATE' ? IconRefresh
                                 : formatted.action === 'DELETE' ? IconTrash
                                 : formatted.action === 'PUBLISH' ? IconExternalLink
                                 : IconHistory;
                      
                      return (
                        <Timeline.Item
                          key={log.id}
                          bullet={<Icon size={12} />}
                          title={formatted.actionLabel}
                          color={formatted.actionColor}
                        >
                          <Text size="xs" c="dimmed">
                            {formatted.performedBy} • {formatted.performedAtFormatted}
                          </Text>
                          {formatted.changedFields.length > 0 && (
                            <Text size="xs" c="dimmed" mt="xs">
                              Changed: {formatted.changedFields.join(', ')}
                            </Text>
                          )}
                        </Timeline.Item>
                      );
                    })}
                  </Timeline>
                ) : (
                  <Timeline active={-1} bulletSize={20} lineWidth={2}>
                    <Timeline.Item
                      bullet={<IconCheck size={12} />}
                      title="Assignment Created"
                    >
                      <Text size="xs" c="dimmed">
                        {new Date(assignment.createdAt).toLocaleString()}
                      </Text>
                    </Timeline.Item>
                    
                    {assignment.updatedAt !== assignment.createdAt && (
                      <Timeline.Item
                        bullet={<IconRefresh size={12} />}
                        title="Last Updated"
                      >
                        <Text size="xs" c="dimmed">
                          {new Date(assignment.updatedAt).toLocaleString()}
                        </Text>
                      </Timeline.Item>
                    )}
                    
                    {assignment.publishedAt && (
                      <Timeline.Item
                        bullet={<IconExternalLink size={12} />}
                        title="Published"
                      >
                        <Text size="xs" c="dimmed">
                          {new Date(assignment.publishedAt).toLocaleString()}
                        </Text>
                      </Timeline.Item>
                    )}
                  </Timeline>
                )}
              </Stack>
            </Card>
          </Stack>
        </ScrollArea>

        {/* Footer Actions */}
        <Box p="md" style={{ borderTop: '1px solid var(--mantine-color-gray-3)' }}>
          {/* Helpful tip for coordinators */}
          {assignment && assignment.status === 'confirmed' && !assignment.publishedAt && !assignment.conflictCode && (
            <Alert color="blue" icon={<IconBulb size={16} />} mb="md">
              <Text size="xs">
                This assignment is confirmed and conflict-free. Click "Publish" to make it visible to doctors.
              </Text>
            </Alert>
          )}
          
          {/* Warning for unpublished with conflicts */}
          {assignment && assignment.conflictCode && (
            <Alert color="orange" icon={<IconAlertTriangle size={16} />} mb="md">
              <Text size="xs">
                This assignment has conflicts ({assignment.conflictCode}). Resolve conflicts before publishing.
              </Text>
            </Alert>
          )}
          
          <Group justify="flex-end" gap="sm">
            <Button variant="light" onClick={onClose} disabled={deleting || unassigning || publishMutation.isPending}>
              Close
            </Button>
            
            {/* Publish Button - Show only for confirmed, unpublished assignments without conflicts */}
            {assignment && 
             assignment.status === 'confirmed' && 
             !assignment.publishedAt && 
             !assignment.conflictCode && (
              <Button
                color="green"
                leftSection={<IconCheck size={16} />}
                onClick={() => handlePublishAssignment(assignment.id)}
                loading={publishMutation.isPending}
                disabled={deleting || unassigning}
              >
                Publish Now
              </Button>
            )}
            
            <Button
              color="red"
              leftSection={<IconTrash size={16} />}
              onClick={() => setShowDeleteModal(true)}
              disabled={!onDelete || publishMutation.isPending}
            >
              Delete
            </Button>
          </Group>
        </Box>
      </Drawer>

      {/* Delete Confirmation Modal */}
      <Modal
        opened={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Confirm Deletion"
        centered
        size="sm"
      >
        <Stack gap="md">
          <Alert
            icon={<IconAlertTriangle size={16} />}
            title="Are you sure?"
            color="red"
            variant="light"
          >
            <Text size="sm">
              This will permanently delete the assignment "{assignment?.title}". 
              This action cannot be undone.
            </Text>
          </Alert>

          <Group justify="flex-end" gap="sm">
            <Button
              variant="light"
              onClick={() => setShowDeleteModal(false)}
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button
              color="red"
              onClick={handleDelete}
              loading={deleting}
              leftSection={<IconTrash size={16} />}
            >
              Delete Assignment
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Edit Assignment Modal */}
      <EditAssignmentModal
        opened={showEditModal}
        onClose={() => setShowEditModal(false)}
        assignment={assignment as any}
        doctors={doctors}
        sessionTimes={sessionTimes}
        onSuccess={() => {
          setShowEditModal(false);
          // Keep drawer open after successful edit so coordinator can publish if ready
          // onClose(); // Removed: Keep drawer open for potential publish action
          notifications.show({
            title: 'Edit Successful',
            message: 'Review changes below and publish when ready',
            color: 'blue',
            icon: <IconCheck size={16} />
          });
        }}
      />
      
      {/* Debug logging for doctor data */}
      {showEditModal && console.log('AssignmentDetailsDrawer - Passing doctors to EditAssignmentModal:', {
        doctorsCount: doctors.length,
        doctors: doctors,
        assignment: assignment,
        sessionTimesCount: sessionTimes.length
      })}
    </>
  );
};

export default AssignmentDetailsDrawer;
